import get from "lodash/get";
import isNil from "lodash/isNil";
import isPlainObject from "lodash/isPlainObject";

const isEmail = (errorMessage: string, value: string) => {
  // email regex taken as a result of this PR thread:
  // https://github.com/friendbuy/fbot/pull/65#discussion_r842035449
  const emailRegEx = RegExp(/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/);

  const isEmailInvalid = !emailRegEx.test(value);

  if (isEmailInvalid) {
    return errorMessage;
  }

  return null;
};

const isEmailList = (errorMessage: string, value: string[]) => {
  if (!value || value.length === 0) {
    return errorMessage;
  }

  for (const email of value) {
    const error = isEmail(errorMessage, email);

    if (error !== null) {
      return `${email} is not a valid email address`;
    }
  }

  return null;
};

const isNotEmpty = (
  errorMessage: string,
  value: string | boolean | undefined | null | number
) => {
  if (!value && value !== 0) {
    return errorMessage;
  }

  return null;
};

export function isString(value?: any): boolean {
  return typeof value === "string";
}

export function isArray(value?: any[]): boolean {
  if (!Array.isArray) {
    return Object.prototype.toString.call(value) === "[object Array]";
  }

  return Array.isArray(value);
}

export function isObject(value?: any): boolean {
  return isPlainObject(value);
}

export function isEmptyObject(obj: any): boolean {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      return false;
    }
  }

  return true;
}

export const isNotSelfReferral = (
  errorMessage: string,
  value: string | string[],
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  const customerEmailAddress = get(data, "customer.email");

  const validate = (email: any) => {
    if (
      typeof customerEmailAddress === "string" &&
      customerEmailAddress === email
    ) {
      return errorMessage;
    }

    return null;
  };

  if (typeof value === "string") {
    return validate(value);
  } else if (isArray(value)) {
    for (const email of value) {
      const error = validate(email);

      if (error !== null) {
        return errorMessage;
      }
    }
  }

  return null;
};

export const hasActiveShopifyReward = (
  errorMessage: string,
  value: string | string[],
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  const shopifyActiveDiscountsCount = get(
    data,
    "shopifyActiveDiscounts.count",
    0
  );
  const shopifyActiveGiftCardsCount = get(
    data,
    "shopifyActiveGiftCards.count",
    0
  );

  if (shopifyActiveDiscountsCount > 0 || shopifyActiveGiftCardsCount > 0) {
    return errorMessage;
  }

  return null;
};

export const isMoreThanLedgerHead = (
  errorMessage: string,
  value: string | string[],
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  const ledgerHead = Number(get(data, "ledgerHead.balance", 0));

  if (Number(value) > ledgerHead) {
    return errorMessage;
  }

  return null;
};

// Validates that if a mobile number is provided, subscribeSms must be 'yes'
export const hasMobileNumberWithoutSubscribeSms = (
  errorMessage: string,
  value: string | string[],
  data?: {
    [index: string]: { [index: string]: string | number | string[] } | string;
  }
) => {
  const mobileNumber = get(data, "mobileNumber");
  const subscribeSms = get(data, "subscribeSms", "no");

  if (mobileNumber && subscribeSms !== "yes") {
    // Return error message if provided, otherwise return a default message
    // This ensures validation still works even if error message is empty
    return errorMessage || " ";
  }

  return null;
};

export const hasSubscribeSmsWithoutMobileNumber = (
  errorMessage: string,
  value: string | string[],
  data?: {
    [index: string]: { [index: string]: string | number | string[] } | string;
  }
) => {
  const mobileNumber = get(data, "mobileNumber");
  const subscribeSms = get(data, "subscribeSms", "no");

  if (subscribeSms === "yes" && !mobileNumber) {
    // Return error message if provided, otherwise return a default message
    // This ensures validation still works even if error message is empty
    return errorMessage || " ";
  }

  return null;
};

const isMobileNumber = (errorMessage: string, value: string) => {
  const mobileNumberRegEx = RegExp(/^(\(?\+?[0-9]*\)?)?[0-9_\- \(\)]*$/);

  const isMobileNumberInvalid =
    !isNil(value) && (!mobileNumberRegEx.test(value) || value.length > 30);

  if (isMobileNumberInvalid) {
    return errorMessage || " ";
  }

  return null;
};

export const isCheckboxChecked = (errorMessage: string, value: string) => {
  if (value !== "yes") {
    return errorMessage || " ";
  }

  return null;
};

export const validationFunctions = {
  hasActiveShopifyReward,
  hasMobileNumberWithoutSubscribeSms,
  hasSubscribeSmsWithoutMobileNumber,
  isEmail,
  isEmailList,
  isMobileNumber,
  isMoreThanLedgerHead,
  isNotEmpty,
  isNotSelfReferral,
  isCheckboxChecked,
};

export const validate = (
  validators: string[][],
  value: string | string[] | boolean,
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  let error = null;
  for (const validator of validators) {
    const validatorFunction = validationFunctions[validator[0]];
    const errorMessage = validator[1];

    if (validatorFunction) {
      error = validatorFunction(errorMessage, value, data);
    }

    if (error) {
      return error;
    }
  }
};
